import React from 'react';
import { useLanguage } from '../context/LanguageProvider';
import { useThemeContext } from '../context/ThemeProvider';
import ActionButton from './common/ActionButton';
import PatternSelector from './PatternSelector';
import { PlaybackControlsProps } from '../types';

export default function PlaybackControls({
  iosAudioUnlocked,
  unlockIOSAudio,
  showDynamicMessage,
  instruments,
  players,
  togglePlayback,
  stopPlayback,
  isPlaying,
  isPaused,
  isDemoMode,
  demoStatus,
  startDemo,
  stopDemo,
  onToggleDrumsetEditor,
  showDrumsetEditor,
  exportPattern,
  triggerImport,
  resetPattern,
  selectedPattern,
  setSelectedPattern,
  loadPresetPattern,
  getAllDrumPatterns,
  fileInputRef,
  importPattern,
  timeSignature,
  setTimeSignature,
  noteDivision,
  setNoteDivision,
  loopCount,
  setLoopCount,
  tempo,
  setTempo,
  TEMPO_MIN,
  TEMPO_MAX,
  validateTempo,
  advancedMode,
  setAdvancedMode,
  isAudioLoading,
  debugMode,
  setDebugMode,
  grooveType,
  setGrooveType,
  shuffleAmount,
  setShuffleAmount,
  swingRatio,
  setSwingRatio
}: PlaybackControlsProps) {
  const { language, changeLanguage, t, SUPPORTED_LANGUAGES } = useLanguage();
  const { theme, changeTheme, getClass, SUPPORTED_THEMES } = useThemeContext();

  const getThemeClass = getClass;
  
  const isIOSDevice = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent);
  const playDisabled = isAudioLoading || ['loading', 'stopping'].includes(demoStatus);
  const demoDisabled = isAudioLoading || ['loading', 'playing', 'stopping'].includes(demoStatus) || (isIOSDevice && isPlaying);
  const stopDisabled = isAudioLoading || demoStatus === 'loading' || (demoStatus === 'idle' && !isPlaying && !isPaused);
  const demoLabel = demoStatus === 'loading' ? t('demoLoading') : demoStatus === 'playing' ? t('demoPlaying') : demoStatus === 'stopping' ? t('demoStop') : t('demo');

  return (
    <>
      {/* 語言和主題選擇器 */}
      <div className="flex justify-end">
        <div className="flex space-x-4">
          <div>
            <label className="mr-2">{t('language')}:</label>
            <select
              value={language}
              onChange={(e) => changeLanguage(e.target.value)}
              className={`border px-2 py-1 rounded ${getThemeClass('border')} ${getThemeClass('select')}`}
            >
              {SUPPORTED_LANGUAGES.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.nativeName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mr-2">{t('theme')}:</label>
            <select
              value={theme}
              onChange={(e) => changeTheme(e.target.value)}
              className={`border px-2 py-1 rounded ${getThemeClass('border')} ${getThemeClass('select')}`}
            >
              {SUPPORTED_THEMES.map(themeOption => (
                <option key={themeOption.id} value={themeOption.id}>
                  {themeOption.icon} {t(`themes.${themeOption.name}`)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* iOS 音訊警告提示 */}
      {!iosAudioUnlocked && /iPhone|iPad|iPod/.test(navigator.userAgent) && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded">
          <div className="flex items-center">
            <span className="text-lg mr-2">🔊</span>
            <div>
              <p className="font-medium">{t('iosAudioSettings')}</p>
              <p className="text-sm">{t('iosAudioInstructions')}</p>
              <button
                onClick={unlockIOSAudio}
                className="mt-2 px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-sm font-medium transition-colors"
              >
                🔓 {t('unlockAudioSystem')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 主控制面板 - 簡化的三按鈕布局 */}
      <div className={`p-4 rounded-lg border ${getThemeClass('border')} ${getThemeClass('background')}`}>
        <div className="flex items-center justify-center space-x-4">
          {/* 播放按鈕 */}
          <button
            onClick={async () => {
              if (!iosAudioUnlocked) {
                await unlockIOSAudio();
              }
              await togglePlayback();
            }}
            disabled={playDisabled}
            className={`px-6 py-3 rounded-lg font-medium text-lg transition-all duration-200 hover:scale-105 active:scale-95 transform-gpu text-center leading-tight ${
              isPlaying 
                ? getThemeClass('buttonWarning')
                : getThemeClass('buttonSuccess')
            } ${playDisabled ? getThemeClass('buttonDisabled') : ''}`}
          >
            <span className="block md:inline">{isPlaying ? '⏸️' : '▶️'}</span>
            <span className="block md:inline md:ml-2 text-xs md:text-base">{isPlaying ? t('pause') : t('play')}</span>
          </button>

          {/* 停止按鈕 */}
          <button
            onClick={stopPlayback}
            disabled={stopDisabled}
            className={`px-6 py-3 rounded-lg font-medium text-lg transition-all duration-200 hover:scale-105 active:scale-95 transform-gpu text-center leading-tight ${
              stopDisabled ? getThemeClass('buttonDisabled') : getThemeClass('buttonDanger')
            }`}
          >
            <span className="block md:inline">⏹️</span>
            <span className="block md:inline md:ml-2 text-xs md:text-base">{t('stop')}</span>
          </button>

          {/* Demo按鈕 */}
          <button
            onClick={async () => {
              if (!iosAudioUnlocked) {
                await unlockIOSAudio();
              }
              await startDemo();
            }}
            disabled={demoDisabled}
            className={`px-6 py-3 rounded-lg font-medium text-lg transition-all duration-200 hover:scale-105 active:scale-95 transform-gpu text-center leading-tight ${
              demoDisabled ? getThemeClass('buttonDisabled') : 
              demoStatus === 'playing' ? getThemeClass('buttonSecondary') : getThemeClass('buttonInfo')
            }`}
          >
            <span className="block md:inline">🎭</span>
            <span className="block md:inline md:ml-2 text-xs md:text-base">{demoLabel}</span>
          </button>
        </div>
      </div>

      {/* 桌面版：參數控制 */}
      <div className="hidden md:flex flex-wrap items-center gap-4 lg:gap-6">
        <div className="flex items-center space-x-2">
          <span className="text-lg">{t('timeSignatureIcon')}</span>
          <label className="hidden lg:inline mr-2">{t('timeSignature')}:</label>
          <select
            value={timeSignature}
            onChange={(e) => setTimeSignature(e.target.value as any)}
            className={`border px-2 py-1 rounded ${getThemeClass('border')} ${getThemeClass('select')}`}
            title={t('timeSignature')}
          >
            <option value="3/4">3/4</option>
            <option value="4/4">4/4</option>
            <option value="6/8">6/8</option>
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <span className="text-lg">{t('noteDivisionIcon')}</span>
          <label className="hidden lg:inline mr-2">{t('noteDivision')}:</label>
          <select
            value={noteDivision}
            onChange={(e) => setNoteDivision(e.target.value as any)}
            className={`border px-2 py-1 rounded ${getThemeClass('border')} ${getThemeClass('select')}`}
            title={t('noteDivision')}
          >
            <option value="8n">8n</option>
            <option value="16n">16n</option>
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <span className="text-lg">🎶</span>
          <label className="hidden lg:inline mr-2">{t('grooveType')}:</label>
          <select
            value={grooveType}
            onChange={(e) => setGrooveType(e.target.value as any)}
            className={`border px-2 py-1 rounded ${getThemeClass('border')} ${getThemeClass('select')}`}
            title={t('grooveType')}
          >
            <option value="normal">{t('normal')}</option>
            <option value="shuffle">{t('shuffle')}</option>
            <option value="swing">{t('swing')}</option>
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <span className="text-lg">{t('loopCountIcon')}</span>
          <label className="hidden lg:inline mr-2">{t('loopCount')}:</label>
          <select
            value={loopCount}
            onChange={(e) => setLoopCount(parseInt(e.target.value))}
            className={`border px-2 py-1 rounded ${getThemeClass('border')} ${getThemeClass('select')}`}
            title={t('loopCount')}
          >
            <option value="0">{t('infiniteLoop')}</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
            <option value="8">8</option>
            <option value="10">10</option>
            <option value="16">16</option>
            <option value="32">32</option>
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <span className="text-lg">{t('tempoIcon')}</span>
          <label className="hidden lg:inline mr-2">{t('tempo')}:</label>
          <input
            type="number"
            min={TEMPO_MIN}
            max={TEMPO_MAX}
            value={tempo}
            onChange={(e) => {
              const validatedTempo = validateTempo(e.target.value);
              setTempo(validatedTempo);
            }}
            onBlur={(e) => {
              const validatedTempo = validateTempo(e.target.value);
              setTempo(validatedTempo);
            }}
            className={`border px-2 py-1 w-16 lg:w-20 rounded ${getThemeClass('border')} ${getThemeClass('input')}`}
            title={`${t('tempoRange')} ${TEMPO_MIN}-${TEMPO_MAX} BPM`}
          />
          <span className="text-xs text-gray-500 hidden lg:inline">BPM</span>
        </div>

        <div className="flex items-center space-x-2">
          <span className="text-lg">{t('advancedModeIcon')}</span>
          <input
            type="checkbox"
            id="advancedMode"
            checked={advancedMode}
            onChange={(e) => setAdvancedMode(e.target.checked)}
            className="mr-1"
          />
          <label htmlFor="advancedMode" className="hidden lg:inline">{t('advancedMode')}</label>
        </div>

        {process.env.NODE_ENV === 'development' && (
          <button
            onClick={() => setDebugMode(!debugMode)}
            className={`flex items-center space-x-1 px-2 lg:px-3 py-1 rounded text-sm font-medium transition-colors ${
              debugMode ? getThemeClass('buttonWarning') : getThemeClass('buttonSecondary')
            }`}
            title={t('toggleDebug')}
          >
            <span>{debugMode ? '🔧' : '🛠️'}</span>
            <span className="hidden lg:inline">{t('debugMode')}</span>
          </button>
        )}

        {/* Debug模式的Groove參數滑槓（桌面版） */}
        {debugMode && grooveType !== 'normal' && (
          <div className={`flex items-center space-x-4 p-2 rounded border ${getThemeClass('surface')} ${getThemeClass('border')}`}>
            <div className={`flex items-center text-sm font-medium ${getThemeClass('text')}`}>
              <span className="mr-1">🔧</span>
              <span>{t('debugParams')}:</span>
            </div>
            
            {grooveType === 'shuffle' && (
              <div className="flex items-center space-x-2">
                <label className={`text-xs font-medium ${getThemeClass('textSecondary')}`}>
                  {t('shuffleAmount')}: {(shuffleAmount * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={shuffleAmount}
                  onChange={(e) => setShuffleAmount(parseFloat(e.target.value))}
                  className="w-20"
                />
              </div>
            )}
            
            {grooveType === 'swing' && (
              <div className="flex items-center space-x-2">
                <label className={`text-xs font-medium ${getThemeClass('textSecondary')}`}>
                  {t('swingRatio')}: {(swingRatio * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="0.8"
                  step="0.01"
                  value={swingRatio}
                  onChange={(e) => setSwingRatio(parseFloat(e.target.value))}
                  className="w-20"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* 手機版：精簡三行布局 */}
      <div className="md:hidden space-y-3">
        {/* 第一行：拍號 + 音符分割 + Groove */}
        <div className="grid grid-cols-3 gap-2">
          <div className="flex items-center space-x-1">
            <span className="text-lg">{t('timeSignatureIcon')}</span>
            <select
              value={timeSignature}
              onChange={(e) => setTimeSignature(e.target.value as any)}
              className={`flex-1 border px-1 py-1 rounded text-xs ${getThemeClass('border')} ${getThemeClass('select')}`}
            >
              <option value="3/4">3/4</option>
              <option value="4/4">4/4</option>
              <option value="6/8">6/8</option>
            </select>
          </div>

          <div className="flex items-center space-x-1">
            <span className="text-lg">{t('noteDivisionIcon')}</span>
            <select
              value={noteDivision}
              onChange={(e) => setNoteDivision(e.target.value as any)}
              className={`flex-1 border px-1 py-1 rounded text-xs ${getThemeClass('border')} ${getThemeClass('select')}`}
            >
              <option value="8n">8n</option>
              <option value="16n">16n</option>
            </select>
          </div>

          <div className="flex items-center space-x-1">
            <span className="text-lg">🎶</span>
            <select
              value={grooveType}
              onChange={(e) => setGrooveType(e.target.value as any)}
              className={`flex-1 border px-1 py-1 rounded text-xs ${getThemeClass('border')} ${getThemeClass('select')}`}
              title={`${t('grooveType')}: ${t(grooveType)}`}
            >
              <option value="normal">{t('normal')}</option>
              <option value="shuffle">{t('shuffle')}</option>
              <option value="swing">{t('swing')}</option>
            </select>
          </div>
        </div>

        {/* 第二行：循環 + 節拍 + 高級模式 */}
        <div className="grid grid-cols-3 gap-2">
          <div className="flex items-center space-x-1">
            <span className="text-lg">{t('loopCountIcon')}</span>
            <select
              value={loopCount}
              onChange={(e) => setLoopCount(parseInt(e.target.value))}
              className={`flex-1 border px-1 py-1 rounded text-xs ${getThemeClass('border')} ${getThemeClass('select')}`}
            >
              <option value="0">∞</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
              <option value="8">8</option>
              <option value="10">10</option>
              <option value="16">16</option>
              <option value="32">32</option>
            </select>
          </div>

          <div className="flex items-center space-x-1">
            <span className="text-lg">{t('tempoIcon')}</span>
            <input
              type="number"
              min={TEMPO_MIN}
              max={TEMPO_MAX}
              value={tempo}
              onChange={(e) => {
                const validatedTempo = validateTempo(e.target.value);
                setTempo(validatedTempo);
              }}
              onBlur={(e) => {
                const validatedTempo = validateTempo(e.target.value);
                setTempo(validatedTempo);
              }}
              className={`flex-1 border px-1 py-1 rounded text-xs ${getThemeClass('border')} ${getThemeClass('input')}`}
              title={`${t('tempoRange')} ${TEMPO_MIN}-${TEMPO_MAX} BPM`}
            />
          </div>

          <div className="flex items-center space-x-1">
            <span className="text-lg">{t('advancedModeIcon')}</span>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={advancedMode}
                onChange={(e) => setAdvancedMode(e.target.checked)}
                className="sr-only"
              />
              <span className={`w-5 h-5 border rounded text-xs flex items-center justify-center ${
                advancedMode ? getThemeClass('button') : `${getThemeClass('border')} ${getThemeClass('background')}`
              }`}>
                {advancedMode ? '✓' : ''}
              </span>
            </label>
            {process.env.NODE_ENV === 'development' && (
              <button
                onClick={() => setDebugMode(!debugMode)}
                className={`ml-1 w-5 h-5 border rounded text-xs flex items-center justify-center ${
                  debugMode ? getThemeClass('buttonWarning') : `${getThemeClass('border')} ${getThemeClass('background')}`
                }`}
                title={t('toggleDebug')}
              >
                {debugMode ? '🔧' : '⚙️'}
              </button>
            )}
          </div>
        </div>

        {/* Debug模式的Groove參數滑槓（手機版） */}
        {debugMode && grooveType !== 'normal' && (
          <div className={`p-2 rounded border ${getThemeClass('surface')} ${getThemeClass('border')}`}>
            <div className={`flex items-center text-xs font-medium ${getThemeClass('text')} mb-2`}>
              <span className="mr-1">🔧</span>
              <span>{t('debugParams')}: {grooveType}</span>
            </div>
            
            {grooveType === 'shuffle' && (
              <div className="space-y-1">
                <label className={`text-xs font-medium ${getThemeClass('textSecondary')} block`}>
                  {t('shuffleAmount')}: {(shuffleAmount * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={shuffleAmount}
                  onChange={(e) => setShuffleAmount(parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>
            )}
            
            {grooveType === 'swing' && (
              <div className="space-y-1">
                <label className={`text-xs font-medium ${getThemeClass('textSecondary')} block`}>
                  {t('swingRatio')}: {(swingRatio * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="0.8"
                  step="0.01"
                  value={swingRatio}
                  onChange={(e) => setSwingRatio(parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* 隱藏的文件輸入 */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={importPattern}
        style={{ display: 'none' }}
      />

      {/* Pattern 選擇器 */}
      <PatternSelector
        selectedPattern={selectedPattern}
        onPatternChange={(pattern: string) => {
          setSelectedPattern(pattern);
          if (pattern) {
            loadPresetPattern(pattern);
          }
        }}
        allPatterns={getAllDrumPatterns()}
      />
    </>
  );
} 